import React, { useState, useRef, useEffect } from 'react';
import { IoMenu, IoClose } from 'react-icons/io5';
import { Category } from '../../types/categoreies';
import axios from 'axios';
import { Config } from '../../API/config';
import { Filters } from '../../types/filter';

const Categories = ({ handleLoading, onChange, resetTrigger, filters }: { handleLoading: (loading: boolean) => void, onChange: (value: Filters) => void, resetTrigger: boolean, filters: Filters }) => {
    const [isLoading, setIsLoading] = useState(true);
    const [categories, setCategories] = useState<Category[]>([]);
    const [learningCategories, setLearningCategories] = useState<Category[]>([]);
    const [selectedSubcategory, setSelectedSubcategory] = useState<null | string>(null);
    const [activeCategory, setActiveCategory] = useState<null | number>(null);
    const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
    const [isAllCategoriesOpen, setIsAllCategoriesOpen] = useState(false);
    const [showLearningCategories, setShowLearningCategories] = useState(false);
    const categoryRefs = useRef<(HTMLDivElement | null)[]>([]);
    const timeoutRef = useRef<NodeJS.Timeout | null>(null);

    const DROPDOWN_INDEX_OFFSET = 10;

    useEffect(() => {
        setShowLearningCategories(false);
        setSelectedSubcategory(null);
    }, [resetTrigger]);

    const fetchCategories = async () => {
        try {
            let { data } = await axios.get(Config.getAllCategories, { withCredentials: true });
            if (data.statusCode === 200) {
                setCategories(data.data);
            }
        } catch (err) {
            console.error('Error fetching categories:', err);
        }
    };

    const fetchLearningCategories = async () => {
        try {
            let { data } = await axios.get(Config.getAllLearningCategories, { withCredentials: true });
            if (data.statusCode === 200) {
                setLearningCategories(data.data);
                setIsLoading(false);
                handleLoading(false);
            }
        } catch (err) {
            console.error('Error fetching learning categories:', err);
        }
    };

    useEffect(() => {
        const fetchData = async () => {
            await Promise.all([fetchLearningCategories(), fetchCategories()]);
        };
        fetchData();
    }, []);

    const findParentCategory = (subcategoryName: string): { category: string, found: boolean } => {
        for (const category of categories) {
            if (category.subCategories.some(sub => sub.name === subcategoryName)) {
                return { category: category.name, found: true };
            }
        }
        for (const category of learningCategories) {
            if (category.subCategories.some(sub => sub.name === subcategoryName)) {
                return { category: category.name, found: true };
            }
        }
        return { category: "", found: false };
    };

    const handleCategorySelect = (category: string, index?: number) => {
        if (index !== undefined) {
            // For mobile menu: toggle subcategories
            setActiveCategory(activeCategory === index ? null : index);
        }
        setSelectedSubcategory(null);
        const updatedFilters = {
            ...filters,
            category: category,
            subCategory: "",
        };
        onChange(updatedFilters);
    };

    const handleSubcategorySelect = (subcategory: string) => {
        setSelectedSubcategory(subcategory);
        setActiveCategory(null);
        setIsMobileMenuOpen(false);
        setIsAllCategoriesOpen(false);

        const { category } = findParentCategory(subcategory);
        const updatedFilters = {
            ...filters,
            category: category || "",
            subCategory: subcategory,
        };
        onChange(updatedFilters);
    };

    const handleMouseEnter = (index: number) => {
        if (timeoutRef.current) {
            clearTimeout(timeoutRef.current);
        }
        setActiveCategory(index);
    };

    const handleMouseLeave = () => {
        timeoutRef.current = setTimeout(() => {
            setActiveCategory(null);
        }, 200);
    };

    const toggleAllCategories = () => {
        setIsAllCategoriesOpen(prev => !prev);
        setActiveCategory(null);
    };

    const toggleCategoryView = () => {
        setShowLearningCategories(prev => !prev);
        setActiveCategory(null);
    };

    const visibleCategories = showLearningCategories
        ? learningCategories.slice(0, 5)
        : categories.slice(0, 5);

    return (
        <>
            {isLoading ? (
                <div className="h-40 bg-gray-300 rounded-lg"></div>
            ) : (
                <div className="w-full bg-Categories shadow-md relative">
                    {/* Mobile Menu Icon */}
                    <div className="flex justify-between items-center p-4 md:hidden">
                        <IoMenu
                            size={28}
                            className="text-white cursor-pointer"
                            onClick={toggleAllCategories}
                        />
                        <h2 className="text-white text-lg">الفئات</h2>
                        {isMobileMenuOpen ? (
                            <IoClose
                                size={28}
                                className="text-white cursor-pointer"
                                onClick={() => setIsMobileMenuOpen(false)}
                            />
                        ) : (
                            <IoMenu
                                size={28}
                                className="text-white cursor-pointer"
                                onClick={() => setIsMobileMenuOpen(true)}
                            />
                        )}
                    </div>

                    {/* Desktop Categories */}
                    <div className="hidden md:flex mx-11 flex-row-reverse items-center justify-center space-x-6 h-16 relative">
                        <IoMenu
                            size={28}
                            className="text-white cursor-pointer absolute left-0 top-1/2 transform -translate-y-1/2"
                            onClick={toggleAllCategories}
                        />
                        <button
                            onClick={toggleCategoryView}
                            className="bg-primary-200 p-4 text-white hover:text-neutral-500 px-4 py-2 rounded-lg transition duration-300"
                        >
                            {showLearningCategories ? 'عرض الفئات العادية' : 'التعليم المدرسي'}
                        </button>
                        {visibleCategories.map((category, index) => (
                            <div
                                key={`${showLearningCategories ? 'learning' : 'regular'}-${index}`}
                                className="relative group"
                                ref={(el) => (categoryRefs.current[index] = el)}
                                onMouseEnter={() => handleMouseEnter(index)}
                                onMouseLeave={handleMouseLeave}
                            >
                                <button
                                    onClick={() => handleCategorySelect(category.name)}
                                    className="flex items-center space-x-2 text-white hover:text-neutral-500 px-4 py-2 rounded-lg transition duration-300"
                                >
                                    <span className="mx-5">{category.name}</span>
                                </button>
                                <div
                                    className={`absolute left-0 mt-2 w-48 overflow-y-auto max-h-72 bg-white rounded-lg shadow-lg z-50 transform transition-all duration-200 ease-in-out ${activeCategory === index
                                        ? 'opacity-100 translate-y-0 visible'
                                        : 'opacity-0 -translate-y-2 invisible'
                                        }`}
                                    onMouseEnter={() => handleMouseEnter(index)}
                                    onMouseLeave={handleMouseLeave}
                                >
                                    {category.subCategories.map((sub) => (
                                        <button
                                            key={sub._id}
                                            onClick={() => handleSubcategorySelect(sub.name)}
                                            className="w-full text-right text-stone-600 hover:text-primary-300 hover:pr-6 px-4 py-2 transition-all duration-200"
                                        >
                                            {sub.name}
                                        </button>
                                    ))}
                                </div>
                            </div>
                        ))}
                    </div>

                    {/* All Categories Dropdown (Desktop & Mobile) */}
                    {isAllCategoriesOpen && (
                        <>
                            <div
                                className="fixed inset-0 bg-black bg-opacity-50 z-40 md:hidden"
                                onClick={() => setIsAllCategoriesOpen(false)}
                            ></div>
                            <div
                                className={`absolute top-16 left-0 w-full md:w-64 bg-white rounded-lg shadow-lg z-50 p-4 md:max-h-96 overflow-y-auto transition-all duration-300 ${isAllCategoriesOpen ? 'max-h-screen' : 'max-h-0'}`}
                            >
                                {/* Learning Categories Section */}
                                <div className="mb-4">
                                    <h3 className="text-right font-bold text-primary-300 mb-2">فئات التعلم</h3>
                                    {learningCategories.map((category, index) => (
                                        <div
                                            key={`learning-${index}`}
                                            className="mb-2 relative group"
                                            onMouseEnter={() => handleMouseEnter(index + DROPDOWN_INDEX_OFFSET)}
                                            onMouseLeave={handleMouseLeave}
                                        >
                                            <button
                                                className="w-full text-right font-medium text-stone-600 hover:text-primary-300"
                                                onClick={() => handleCategorySelect(category.name)}
                                            >
                                                {category.name}
                                            </button>
                                            <div
                                                className={`mt-2 space-y-2 overflow-y-auto max-h-28 ${activeCategory === (index + DROPDOWN_INDEX_OFFSET)
                                                    ? 'block'
                                                    : 'hidden group-hover:block'
                                                    }`}
                                            >
                                                {category.subCategories.map((sub) => (
                                                    <button
                                                        key={sub._id}
                                                        onClick={() => handleSubcategorySelect(sub.name)}
                                                        className="w-full text-right text-stone-600 bg-slate-200 hover:text-primary-300 px-2 py-1 transition"
                                                    >
                                                        {sub.name}
                                                    </button>
                                                ))}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                                {/* Regular Categories Section */}
                                <div className="mb-4">
                                    <h3 className="text-right font-bold text-primary-300 mb-2">فئات أخرى</h3>
                                    {categories.map((category, index) => (
                                        <div
                                            key={`regular-${index}`}
                                            className="mb-2 relative group"
                                            onMouseEnter={() => handleMouseEnter(index + DROPDOWN_INDEX_OFFSET + learningCategories.length)}
                                            onMouseLeave={handleMouseLeave}
                                        >
                                            <button
                                                className="w-full text-right font-medium text-stone-600 hover:text-primary-300"
                                                onClick={() => handleCategorySelect(category.name)}
                                            >
                                                {category.name}
                                            </button>
                                            <div
                                                className={`mt-2 space-y-2 overflow-y-auto max-h-28 ${activeCategory === (index + DROPDOWN_INDEX_OFFSET + learningCategories.length)
                                                    ? 'block'
                                                    : 'hidden group-hover:block'
                                                    }`}
                                            >
                                                {category.subCategories.map((sub) => (
                                                    <button
                                                        key={sub._id}
                                                        onClick={() => handleSubcategorySelect(sub.name)}
                                                        className="w-full text-right text-stone-600 bg-slate-200 hover:text-primary-300 px-2 py-1 transition"
                                                    >
                                                        {sub.name}
                                                    </button>
                                                ))}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </>
                    )}

                    {/* Mobile Subcategories Slider */}
                    {isMobileMenuOpen && (
                        <>
                            <div
                                className="fixed inset-0 bg-black bg-opacity-50 z-40 md:hidden"
                                onClick={() => setIsMobileMenuOpen(false)}
                            ></div>
                            <div
                                className={`fixed top-0 right-0 h-full w-3/4 bg-white z-50 p-4 overflow-y-auto transform transition-transform duration-300 ease-in-out ${isMobileMenuOpen ? 'translate-x-0' : 'translate-x-full'}`}
                            >
                                <div className="flex justify-between items-center mb-4">
                                    <h2 className="text-lg font-bold text-primary-300">القائمة</h2>
                                    <IoClose
                                        size={28}
                                        className="cursor-pointer text-stone-600"
                                        onClick={() => setIsMobileMenuOpen(false)}
                                    />
                                </div>
                                {visibleCategories.map((category, index) => (
                                    <div key={`${showLearningCategories ? 'learning' : 'regular'}-${index}`} className="mb-4">
                                        <button
                                            className="w-full text-right font-bold text-primary-300"
                                            onClick={() => handleCategorySelect(category.name, index)}
                                        >
                                            {category.name}
                                        </button>
                                        <div
                                            className={`mt-2 space-y-2 ${activeCategory === index ? 'block' : 'hidden'}`}
                                        >
                                            {category.subCategories.map((sub) => (
                                                <button
                                                    key={sub._id}
                                                    onClick={() => handleSubcategorySelect(sub.name)}
                                                    className="w-full text-right text-stone-600 hover:text-primary-300 px-2 py-1 transition"
                                                >
                                                    {sub.name}
                                                </button>
                                            ))}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </>
                    )}
                </div>
            )}
        </>
    );
};

export default Categories;